import { DraftSnapshot } from "@/entities/DataAssociation/Draft.model";
import { FileSnapshot } from "@/entities/DataAssociation/File.model";
import { getLocalStorage, setLocalStorage } from "@/features/LocalStorage";
import { LocalStorageKey } from "@/features/LocalStorage";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useFileList } from "./useFileList";
import { useAtomValue } from "jotai";
import { currentDraftAtom, selectedFileAtom } from "../state";

type RecentlyViewedItem = {
    fileSnapshot: FileSnapshot;
    draft?: DraftSnapshot;
    viewedAt: string;
};

export function useLabelingEmptyState() {
    const localStorageData = getLocalStorage(LocalStorageKey.Labeling);
    const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedItem[]>(
        localStorageData?.recentlyViewed || [],
    );

    const { data: selectedFile } = useAtomValue(selectedFileAtom);
    const { data: currentDraft } = useAtomValue(currentDraftAtom);
    const { unlabeledFiles, cleanedFiles, isLoading } = useFileList();

    const pushRecentlyViewed = useCallback(
        (item: { fileSnapshot: FileSnapshot; draft?: DraftSnapshot }) => {
            setRecentlyViewed((prev) => {
                const newItem = {
                    ...item,
                    viewedAt: dayjs().format("YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]"),
                };
                const filtered = prev
                    .filter(
                        (existing) =>
                            existing.fileSnapshot.id !== item.fileSnapshot.id,
                    )
                    .sort(
                        (a, b) =>
                            new Date(b.viewedAt).getTime() -
                            new Date(a.viewedAt).getTime(),
                    );
                const updated = [newItem, ...filtered];
                return updated.slice(0, 3);
            });
        },
        [],
    );

    useEffect(() => {
        setLocalStorage(LocalStorageKey.Labeling, {
            recentlyViewed,
        });
    }, [recentlyViewed]);

    useEffect(() => {
        if (selectedFile) {
            const recentlyViewedItem: RecentlyViewedItem = {
                fileSnapshot: selectedFile,
                viewedAt: dayjs().format("YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]"),
            };

            if (currentDraft) {
                recentlyViewedItem.draft = {
                    id: currentDraft.id,
                    status: currentDraft.status,
                    fileId: currentDraft.fileId,
                    createdBy: currentDraft.ownerId,
                    createdAt: currentDraft.createdAt,
                    committedAt: null,
                };
            }

            pushRecentlyViewed(recentlyViewedItem);
        }
    }, [selectedFile, currentDraft, pushRecentlyViewed]);

    const parsedRecentlyViewed = useMemo(() => {
        return recentlyViewed
            .map((item) => ({
                href: `/beta/companies/${item.fileSnapshot.companyId}/labeling/files/${item.fileSnapshot.id}${item.draft ? `/drafts/${item.draft.id}` : ""}`,
                ...item,
            }))
            .sort(
                (a, b) =>
                    new Date(b.viewedAt).getTime() -
                    new Date(a.viewedAt).getTime(),
            );
    }, [recentlyViewed]);

    const quickActions = useMemo(() => {
        const mostRecentUnlabeledFile = [...unlabeledFiles].sort(
            (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
        )[0];
        const actions: {
            label: string;
            href: string | null;
            isLoading: boolean;
        }[] = [];

        if (recentlyViewed.length > 0) {
            const mostRecentlyViewed = parsedRecentlyViewed[0];
            actions.push({
                label: `Open most recently viewed file`,
                href: mostRecentlyViewed.href,
                isLoading: false,
            });
        }

        if (mostRecentUnlabeledFile) {
            actions.push({
                label: `Open most recently created unlabeled file`,
                href: `/beta/companies/${mostRecentUnlabeledFile.companyId}/labeling/files/${mostRecentUnlabeledFile.id}`,
                isLoading: false,
            });
        } else if (cleanedFiles.length > 0) {
            actions.push({
                label: `Open most recently created file`,
                href: `/beta/companies/${cleanedFiles[0].companyId}/labeling/files/${cleanedFiles[0].id}`,
                isLoading: false,
            });
        }

        return actions;
    }, [unlabeledFiles, cleanedFiles, isLoading, parsedRecentlyViewed]);

    return {
        recentlyViewed: parsedRecentlyViewed,
        quickActions,
        pushRecentlyViewed,
    };
}
